import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useGet } from '../hooks';
import { StoreData } from '@/types/store';
import { OrderDetails, OrderItem } from '@/types/orders';
import { extractCheckId } from '@/utils/common';
import { collectNotes } from '@/utils/orderNotes';

// Import the proper OrderData interface from sample data
interface OrderData {
  deliveryId: string;
  checkId: string;
  channel: { id: string; label: string };
  orderTime: string;
  orderDate?: string;
  status: string;
  hasNotes: boolean;
  orderNotes?: string;
  orderDateTime?: string;
  [key: string]: unknown;
}

// Context state interface
interface OrdersContextState {
  store: StoreData | null;
  data: OrderData[] | null;
  loading: boolean;
  error: string | null;
  fetchOrders: (dateFrom?: Date, dateTo?: Date) => void;
  refetch: () => void;
}

// Context provider props
interface OrdersProviderProps {
  children: ReactNode;
  store: StoreData | null;
  restaurantId: number | null;
}

// Create the context with default values
const OrdersContext = createContext<OrdersContextState | undefined>(undefined);

const ORDERS_POLL_INTERVAL_MS = 15000;

const getStartOfDayMs = (date: Date): number => {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  return localDate.getTime();
};

const getEndOfDayMs = (date: Date): number => {
  const localDate = new Date(date);
  localDate.setHours(23, 59, 59, 999);
  return localDate.getTime();
};

const hasItemNotes = (items: OrderItem[] | undefined): boolean =>
  (items ?? []).some(
    item =>
      Boolean(item.notes?.trim()) ||
      hasItemNotes(item.modifiers) ||
      hasItemNotes(item.mains) ||
      hasItemNotes(item.sides)
  );

const buildNotesPreview = (orderDetails: OrderDetails): string | undefined => {
  const notes = collectNotes(orderDetails).map(({ label, text }) =>
    label ? `${label}: ${text}` : text
  );

  return notes.length > 0 ? notes.join(' • ') : undefined;
};

const mapOrderDetailsToOrderData = (orderDetails: OrderDetails): OrderData => {
  let channelId: string;
  let channelLabel: string;

  if (typeof orderDetails.channel === 'object') {
    channelId = orderDetails.channel.name;
    channelLabel =
      orderDetails.channel.readableName || orderDetails.channel.name;
  } else {
    channelId = String(orderDetails.channel);
    channelLabel = channelId;
  }

  return {
    deliveryId: orderDetails.externalReference || '',
    checkId: extractCheckId(orderDetails.posReference),
    channel: { id: channelId, label: channelLabel },
    orderTime: orderDetails.orderTime,
    orderDate: orderDetails.orderDate,
    orderDateTime:
      orderDetails.orderDate && orderDetails.orderTime
        ? `${orderDetails.orderDate}T${orderDetails.orderTime}`
        : orderDetails.orderTime,
    status: orderDetails.cancelled ? 'cancelled' : '',
    hasNotes:
      Boolean(orderDetails.orderNotes?.trim()) ||
      hasItemNotes(orderDetails.items),
    orderNotes: buildNotesPreview(orderDetails),
    orderDetails,
  };
};

// Context Provider Component
export const OrdersProvider: React.FC<OrdersProviderProps> = ({
  children,
  store,
  restaurantId,
}) => {
  // Use the fetch hook with immediate=false to prevent fetching on mount with empty URL
  // The actual fetch is triggered by fetchOrders() called from the Orders component
  const { data, loading, error, refetch } = useGet<OrderDetails[]>(
    '',
    {},
    false,
    true
  );

  const lastEndpointRef = useRef<string | null>(null);

  // Create a fetchOrders function for manual triggering with date parameter
  const fetchOrders = useCallback(
    (from?: Date, to?: Date) => {
      if (!restaurantId) {
        console.warn('Restaurant ID is not available');
        return;
      }

      // Fall back to today if the caller passed an Invalid Date (e.g. from a stale
      // picker state or serialised date that didn't round-trip). Sending NaN to
      // ?dateFrom= would 500 the backend with a Long parse error.
      const fromCandidate = from instanceof Date ? from : new Date();
      const validFrom = Number.isFinite(fromCandidate.getTime());
      const validTo = !to || Number.isFinite(to.getTime());
      const targetFrom = validFrom ? fromCandidate : new Date();
      const targetTo = validTo ? (to ?? targetFrom) : targetFrom;
      if (!validFrom || !validTo) {
        console.warn(
          'fetchOrders received an invalid Date — falling back to today:',
          !validFrom ? fromCandidate : to
        );
      }
      const dateFrom = getStartOfDayMs(targetFrom);
      const dateTo = getEndOfDayMs(targetTo);

      // Use restaurantId from props for API calls
      const endpoint =
        process.env.REACT_APP_API_DOMAIN +
        `orders/stores/${restaurantId}?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      lastEndpointRef.current = endpoint;
      refetch(endpoint);
    },
    [restaurantId, refetch]
  );

  // Poll the last-fetched orders endpoint so new orders (and their sound) are picked up
  // regardless of which in-app tab is active.
  useEffect(() => {
    const handle = window.setInterval(() => {
      if (lastEndpointRef.current) {
        refetch(lastEndpointRef.current);
      }
    }, ORDERS_POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [refetch]);

  const mappedData =
    Array.isArray(data) && data ? data.map(mapOrderDetailsToOrderData) : null;

  // Context value - use mock data as fallback when API data is undefined
  const contextValue: OrdersContextState = {
    store,
    data: mappedData,
    loading: loading || false,
    error: error,
    fetchOrders,
    refetch,
  };

  return (
    <OrdersContext.Provider value={contextValue}>
      {children}
    </OrdersContext.Provider>
  );
};

// Custom hook to use the Orders context
export const useOrders = (): OrdersContextState => {
  const context = useContext(OrdersContext);

  if (context === undefined) {
    throw new Error('useOrders must be used within an OrdersProvider');
  }

  return context;
};

// Export the context for advanced use cases (optional)
export { OrdersContext };
